import CallSplitIcon from "@mui/icons-material/CallSplit";
import ControlPointDuplicateIcon from "@mui/icons-material/ControlPointDuplicate";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StarRateIcon from "@mui/icons-material/StarRate";
import SubjectIcon from "@mui/icons-material/Subject";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Button, FormControlLabel, Grid, List, ListItem } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import { Module, SkinnyModule } from "../../types/Module.type";
import ServiceHierarchySelect from "./fields/ServiceHierarchySelect";
import useOrganizationModule from "./hooks/useOrganizationModules";
import ModuleService from "./Module.service";
import AcxSwitch from "components/UI/AcxSwitch";
import { styled } from "@mui/material/styles";

// Styled components for repeated styles
const Accordion = styled(MuiAccordion)(({ theme }) => ({
    width: "100%",
    borderRadius: 4,
    "&:before": {
        display: "none",
    },
    margin: "4px 0",
    "&.MuiAccordion-expanded": {
        margin: "16px 0",
    },
    boxShadow:
        "rgba(0, 0, 0, 0.02) 0px 4px 1px -1px, rgba(0, 0, 0, 0.02) 0px 1px 1px 0px, rgba(0, 0, 0, 0.02) 0px 2px 6px 0px",
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
    marginBottom: -1,
    minHeight: 72,
    "&.MuiAccordionSummary-expanded": {
        minHeight: 72,
    },
    "& .MuiAccordionSummary-content": {
        display: "flex",
        alignItems: "center",
        margin: "12px 0",
        justifyContent: "space-between",
    },
    maxHeight: "200px",
    overflow: "hidden",
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const Shape = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
    width: 24,
    height: 24,
    minWidth: 24,
    minHeight: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#ffffff",
    fontSize: 14,
    fontStyle: "bold",
    marginRight: 12,
    borderRadius: "50%",
}));

const ListPrimaryText = styled("div")({
    fontWeight: 800,
    fontSize: 13,
    display: "block",
});

const ListSecondaryText = styled("div")({
    color: "#1f1f1f",
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    paddingTop: 6,
});

const LineLeft = styled("div")({
    display: "flex",
    alignItems: "center",
    flexBasis: "60%",
    flexShrink: 0,
});

const LineMiddleContent = styled("div")({
    display: "flex",
    flexBasis: "20%",
    flexShrink: 0,
    alignSelf: "start",
});

const LineRightContent = styled("div")({
    textAlign: "right",
    flexBasis: "20%",
    flexShrink: 0,
    alignSelf: "start",
    paddingRight: 8,
});

const TruncateName = styled("div")({
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 4,
    display: "-webkit-box",
    textOverflow: "ellipsis",
    overflow: "hidden",
});

const DescriptionContainer = styled(Typography)(({ theme }) => ({
    fontSize: 12,
    color: theme.palette.text.secondary,
    width: "100%",
    marginRight: "1rem",
    marginLeft: "1rem",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 6,
    display: "-webkit-box",
    textOverflow: "ellipsis",
    overflow: "hidden",
}));

const EditButton = styled(Button)({
    textTransform: "none",
    fontWeight: "bold",
});

const ListStyled = styled(List)(({ theme }) => ({
    width: "100%",
    backgroundColor: theme.palette.background.paper,
    borderTop: "1px solid #e5e5e5",
}));

const ResponseTypeIcon = styled("span")({
    width: 18,
    height: 18,
    marginRight: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
});

const FilterIcon = styled(FilterListIcon)({
    marginLeft: 6,
    cursor: "pointer",
    width: 18,
    height: 18,
});

const PageContainer = styled("div")({
    width: "80%",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: 20,
});

interface Props {
    orgId: string;
}

const OrganizationModulesList: React.FC<
    React.PropsWithChildren<React.PropsWithChildren<Props>>
> = observer(({ orgId }) => {
    const authStore = useStore(AuthStore);
    const [openModules, setOpenModules] = useState({});
    const [moduleDetails, setModuleDetails] = useState<Record<string, Module>>(
        {},
    );
    const { modules, isLoading, refetch, duplicate } =
        useOrganizationModule(orgId);
    const [filteredModules, setFilteredModules] = useState<SkinnyModule[]>([]);
    const [selectedHierarchyOptions, setSelectedHierarchyOptions] = useState<
        string[]
    >([]);
    const [showFilter, setShowFilter] = useState(false);
    const [showInactiveModules, setShowInactiveModules] = useState(false);

    useEffect(() => {
        if (selectedHierarchyOptions?.length) {
            const modulesToShow = modules?.filter((module) =>
                module.organizationStructureMemberIds.some((memberId) =>
                    selectedHierarchyOptions.includes(memberId),
                ),
            );
            setFilteredModules(modulesToShow || []);
        } else {
            if (showInactiveModules) {
                setFilteredModules(modules || []);
            } else {
                const modulesToShow = modules?.filter(
                    (module) => module.isActive,
                );
                setFilteredModules(modulesToShow || []);
            }
        }
    }, [selectedHierarchyOptions, modules, isLoading, showInactiveModules]);

    const questionTypes = {
        "Scaled Response": {
            icon: (
                <ResponseTypeIcon>
                    <SubjectIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
        "Tag Response": {
            icon: (
                <ResponseTypeIcon>
                    <LocalOfferIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
        "Star Rating": {
            icon: (
                <ResponseTypeIcon>
                    <StarRateIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
        "Scaled Boolean Response": {
            icon: (
                <ResponseTypeIcon>
                    <CallSplitIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
        "Scored Tag Response": {
            icon: (
                <ResponseTypeIcon>
                    <TrendingUpIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
        "Question Group": {
            icon: (
                <ResponseTypeIcon>
                    <GroupWorkIcon sx={{ width: 18, height: 18, mr: 0.5 }} />
                </ResponseTypeIcon>
            ),
        },
    };

    const handleChange = (panel, moduleId) => (event, newExpanded) => {
        fetchModuleDetails(moduleId);
        setOpenModules((prev) => ({
            ...prev,
            [panel]: !prev[panel],
        }));
    };

    const fetchModuleDetails = async (moduleId: string) => {
        if (!moduleDetails[moduleId]) {
            const module = await ModuleService.getModule(moduleId);
            setModuleDetails((prev) => ({
                ...prev,
                [moduleId]: module,
            }));
            return module;
        }

        return moduleDetails[moduleId];
    };

    const handleModuleToggle = async (module: SkinnyModule) => {
        let moduleToToggle = moduleDetails[module.id];
        if (!moduleToToggle) {
            moduleToToggle = await fetchModuleDetails(module.id);
        }
        await ModuleService.toggleModule(moduleToToggle);
        refetch();
    };

    const toggleFilter = () => {
        setShowFilter((prev) => !prev);
    };

    return (
        <>
            <PageContainer>
                <Grid
                    sx={{
                        marginTop: 1,
                        marginBottom: 1,
                    }}
                    container
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item xs={7}>
                        <Typography variant="subtitle1">
                            Modules{" "}
                            <FilterIcon
                                onClick={toggleFilter}
                                color={showFilter ? "secondary" : "inherit"}
                            />
                        </Typography>
                    </Grid>

                    {authStore.canUserEdit("Modules") && (
                        <Grid
                            container
                            item
                            xs={5}
                            justifyContent="flex-end"
                            alignItems="center"
                        >
                            <Grid
                                container
                                item
                                xs={7}
                                justifyContent="flex-end"
                            >
                                <FormControlLabel
                                    sx={{ marginRight: "0.75rem" }}
                                    control={
                                        <AcxSwitch
                                            onChange={() => {
                                                setOpenModules({});
                                                setShowInactiveModules(
                                                    (prev) => !prev,
                                                );
                                            }}
                                        />
                                    }
                                    label="Display Inactive"
                                />
                            </Grid>
                            <Grid
                                container
                                item
                                xs={5}
                                justifyContent="flex-end"
                            >
                                <Link className="pendo-ignore"
                                    to={`/focus/modules/${orgId}/add`}
                                    style={{ textDecoration: "none" }}
                                >
                                    <AcxButton
                                        color="primary"
                                        sx={{
                                            margin: 0,
                                            maxWidth: "max-content",
                                        }}
                                    >
                                        Add Module
                                    </AcxButton>
                                </Link>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
                {showFilter && (
                    <Grid
                        sx={{
                            marginTop: 1,
                            marginBottom: 1,
                        }}
                        container
                    >
                        <ServiceHierarchySelect
                            orgId={orgId}
                            value={selectedHierarchyOptions}
                            onChange={(val: string[]) =>
                                setSelectedHierarchyOptions(val)
                            }
                        />
                    </Grid>
                )}
                {filteredModules?.map((module, i) => (
                    <Accordion
                        key={i}
                        square
                        expanded={openModules[i] || false}
                        onChange={handleChange(i, module.id)}
                        TransitionProps={{ unmountOnExit: true }}
                    >
                        <AccordionSummary
                            aria-controls="panel1d-content"
                            id="panel1d-header"
                            sx={
                                module.isActive
                                    ? {}
                                    : {
                                          backgroundColor: "#c7c7c7",
                                          borderRadius: 4,
                                      }
                            }
                        >
                            {showInactiveModules && (
                                <FormControlLabel
                                    control={
                                        <AcxSwitch
                                            onChange={() =>
                                                handleModuleToggle(module)
                                            }
                                            checked={module.isActive}
                                            disabled={isLoading}
                                        />
                                    }
                                    aria-label="toggle-active"
                                    label=""
                                    onClick={(event) => event.stopPropagation()}
                                    onFocus={(event) => event.stopPropagation()}
                                />
                            )}
                            <Typography
                                component="span"
                                sx={
                                    module.isActive
                                        ? {
                                              fontSize: 16,
                                              flexBasis: "20%",
                                              flexShrink: 0,
                                              display: "flex",
                                              alignItems: "center",
                                              fontWeight: "bold",
                                              maxHeight: "200px",
                                              color: "black",
                                          }
                                        : {
                                              fontSize: 16,
                                              flexBasis: "20%",
                                              flexShrink: 0,
                                              display: "flex",
                                              alignItems: "center",
                                              fontWeight: "bold",
                                              maxHeight: "200px",
                                              color: "gray",
                                          }
                                }
                            >
                                {openModules[i] ? (
                                    <KeyboardArrowDownIcon />
                                ) : (
                                    <KeyboardArrowRightIcon />
                                )}{" "}
                                <TruncateName>{module.name}</TruncateName>
                            </Typography>

                            <DescriptionContainer>
                                {module.description}
                            </DescriptionContainer>
                            {authStore.canUserEdit("Modules") && (
                                <div>
                                    <Link className="pendo-ignore"
                                        to={`/focus/modules/${orgId}/${module.id}`}
                                        style={{ textDecoration: "none" }}
                                    >
                                        <EditButton
                                            color="primary"
                                            startIcon={<EditOutlinedIcon />}
                                        >
                                            Edit
                                        </EditButton>
                                    </Link>
                                </div>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            {!!moduleDetails[module.id] ? (
                                <ListStyled>
                                    {moduleDetails[module.id].questions.map(
                                        (question, i) => (
                                            <ListItem
                                                sx={{
                                                    paddingLeft: 0,
                                                    paddingRight: 0,
                                                }}
                                                key={i}
                                            >
                                                <LineLeft>
                                                    <Shape>{i + 1}</Shape>
                                                    <div>
                                                        <ListPrimaryText>
                                                            {
                                                                question.questionText
                                                            }
                                                        </ListPrimaryText>
                                                        <ListSecondaryText>
                                                            {
                                                                questionTypes[
                                                                    question
                                                                        .answerType
                                                                        .answerTypeName
                                                                ]?.icon
                                                            }{" "}
                                                            {question.tags
                                                                ?.map(
                                                                    (tag) =>
                                                                        tag.value,
                                                                )
                                                                ?.join(", ")}
                                                        </ListSecondaryText>
                                                    </div>
                                                </LineLeft>
                                                <LineMiddleContent>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: theme.palette
                                                                .text.secondary,
                                                            width: "100%",
                                                        }}
                                                    >
                                                        Score: {question.score}
                                                    </Typography>
                                                </LineMiddleContent>
                                                <LineRightContent>
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: theme.palette
                                                                .text.secondary,
                                                            width: "100%",
                                                        }}
                                                    >
                                                        {
                                                            question.answerType
                                                                .answerTypeName
                                                        }
                                                    </Typography>
                                                </LineRightContent>
                                            </ListItem>
                                        ),
                                    )}
                                    <Grid
                                        sx={{
                                            marginTop: 2,
                                            marginBottom: 1,
                                        }}
                                        container
                                        alignItems="center"
                                        justifyContent="flex-end"
                                    >
                                        <Grid item>
                                            <Button
                                                color="secondary"
                                                startIcon={
                                                    <ControlPointDuplicateIcon />
                                                }
                                                sx={{
                                                    textTransform: "none",
                                                    fontWeight: "bold",
                                                }}
                                                onClick={() => {
                                                    duplicate(
                                                        moduleDetails[
                                                            module.id
                                                        ],
                                                    );
                                                    setOpenModules({});
                                                }}
                                                disabled={
                                                    !authStore.canUserEdit(
                                                        "Modules",
                                                    )
                                                }
                                            >
                                                Duplicate Module
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </ListStyled>
                            ) : (
                                <AcxLoadingIndicator
                                    alternate="PuffLoader"
                                    size={64}
                                />
                            )}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </PageContainer>
        </>
    );
});

export default OrganizationModulesList;
